import { ENDPOINTS, getFullUrl } from "./config";
export class BlogService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }

  static getRole(): string | null {
    return localStorage.getItem("auth_user");
  }

  static async createNewBlogs(data: FormData) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.BLOG.ADD_NEW_BLOG), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: data,
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to create new blogs",
      };
    }
  }

  static async updateNewBlogs(blogId: string, data: FormData | any) {
    try {
      const headers: HeadersInit = {
        Authorization: `Bearer ${this.getToken()}`,
      };

      // If NOT FormData, set JSON content type
      if (!(data instanceof FormData)) {
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.UPDATE_BLOG).replace(":id", blogId)}`,
        {
          method: "PUT",
          headers,
          body: data instanceof FormData ? data : JSON.stringify(data),
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to update the blogs",
      };
    }
  }

  static async deleteBlogs(blogId: string) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.DELETE_BLOG).replace(":id", blogId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to delete the blog",
      };
    }
  }

  static async getBlogsDetails(blogId: string) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.GET_BLOG_DETAILS).replace(":id", blogId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to load the blog details",
      };
    }
  }

  static async addNewBlogContent(
    data: {
      blog_id: string;
      title: string;
      description: string;
    },
    blogId: string,
  ) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.ADD_NEW_BLOG_CONTENT).replace(
          ":id",
          blogId,
        )}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to add new content in the blog",
      };
    }
  }

  static async updateBlogContent(contentId: string, data: unknown) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.UPDATE_BLOG_CONTENT).replace(
          ":contentId",
          contentId,
        )}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update the blog content",
      };
    }
  }

  static async deleteBlogContents(contentId: number) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.DELETE_BLOG_CONTENT).replace(
          ":contentId",
          String(contentId),
        )}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete the blog content",
      };
    }
  }

  static async getAllBlog(page: number, limit: number, search: string) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
      });

      const url = `${getFullUrl(
        ENDPOINTS.BLOG.GET_ALL_BLOG,
      )}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch blogs",
      };
    }
  }

  static async fetchVideos(page: number, pageSize: number, name: string) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      });

      if (name) {
        params.set("name", name);
      }

      const url = `${getFullUrl(
        ENDPOINTS.BLOG.FETCH_VIDEOS,
      )}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch blogs",
      };
    }
  }

  static async deleteVideos(videoId: number) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.BLOG.DELETE_VIDEOS).replace(
          ":id",
          videoId.toString(),
        )}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to delete the video",
      };
    }
  }

  static async addVideo(data: FormData): Promise<any> {
    try {
      const response = await fetch(`${getFullUrl(ENDPOINTS.BLOG.ADD_VIDEO)}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: data,
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to add the video",
      };
    }
  }
}
